import { ActivePanel, AddOnType, FeatureTierLevel, FeatureTierLevelMap, FeatureType, GetAddOnResponse, GetPointResponse, GetTopicResponse, IntegrationException, IntentType, MapDrawer, OnboardingState, OrganizationPermissionLevel, PreviewType, StandardProAddOns, UserInfo } from '../types';
import { LitElement, css, html, unsafeCSS } from 'lit';
import { customElement, query, state } from 'lit/decorators';
import { getFromLocalStorage, isDefined, show_confetti, undefinedIfEmpty } from '../helpers/utils';
import { getQRCode, getQueryStringParameters } from '../helpers/urls';
import { logAsLead, setIntercomUserDetails } from '../helpers/intercom';
import { renderOrganizationLogo, renderProxiLogo } from '../helpers/files';
import { getNewUiUrl } from '../helpers/auth-cookie';

import { ContextMenu } from '../components/nav-bar/context-menu';
import { Dialog } from '@material/mwc-dialog';
import { LeaveSpreadsheetModal } from '../components/leave-spreadsheet-modal';
import { MapComponent } from '../components/map-component';
import { MapHeader } from '../components/common/map-header';
import { OnboardingModal } from '../components/onboarding-modal';
import { StateController } from '../helpers/state/state-management/state-controller';
import { UpgradeDialogElement } from '../components/upgrade-dialog';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { detectMobileViewport } from '../helpers/device';
import { electionResultsAreLive } from '../helpers/api/election-api';
import featuresInfo from '../helpers/state/features-info';
import { getInitials } from '../helpers/strings';
import { getRelativeTime } from '../helpers/date';
import { heights } from '../style/variables';
import { logWithAmplitude } from '../helpers/api/api';
import organizationInfo from '../helpers/state/organization-info';
import pointCache from '../helpers/state/point-cache';
import { registerWalkthroughAfterUpdate } from '../helpers/callout-helpers';
import { removeInertFromVaadinOverlay } from '../helpers/inert';
import { saveEmail } from '../helpers/api/account-api';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';

@customElement('admin-portal-page')
export class AdminPortalPage extends LitElement {
    amplitude_callsite: string = 'Admin Portal';

    @query('#onboardingModal')
    onboardingModal!: Dialog;

    @query('#customAdvancedInsightsModal')
    customAdvancedInsightsModal?: Dialog;

    @query('#onboardingModalComponent')
    onboardingModalComponent!: OnboardingModal;

    @query('#signinModalComponent')
    signinModalComponent!: Dialog;

    @query('#closeSpreadsheetModalDialog')
    closeSpreadsheetModalDialog!: Dialog;

    @query('#closeSpreadsheetModal')
    closeSpreadsheetModalComponent!: LeaveSpreadsheetModal;

    @query('#upgradeDialogComponent')
    upgradeDialogComponent?: Dialog;

    @query('#participantDetailsModal')
    participantDetailsModal?: Dialog;

    @query('#upgradeDialogInnerComponent')
    upgradeDialogInnerComponent!: UpgradeDialogElement;

    @query('#mapComponentWrapper')
    mapComponentWrapper!: MapComponent;

    @query('#addToMapModal')
    addToMapModal!: Dialog;

    @query('#getRouteModal')
    getRouteModal!: Dialog;

    @query('#accountMenu')
    accountMenu!: ContextMenu;

    @query('#map-header')
    mapHeader!: MapHeader;

    @query('#mapHeaderButtons')
    mapHeaderButtons!: HTMLElement;

    @state()
    focusedPoints: GetPointResponse[] = [];

    @state()
    activePanel: ActivePanel = ActivePanel.None;

    @state()
    activeAddOn?: GetAddOnResponse = undefined;

    @state()
    showMapComponent: boolean = true;

    @state()
    onboardingStep?: OnboardingState = undefined;

    @state()
    showAdvancedInsightsModal: boolean = false;

    @state()
    openEditStyle: boolean = false;

    @state()
    userPreview: boolean = false;

    @state()
    userPreviewMode: PreviewType = PreviewType.Desktop;

    @state()
    mobilePreview: boolean = false;

    @state()
    topics: GetTopicResponse[] = [];

    @state()
    qrCode: string = '';

    @state()
    hasOpenedPanel: boolean = false;

    @state()
    isEditing: boolean = false;

    @state()
    allTopicUsers: UserInfo[] = [];

    @state()
    fullWidthPanelOpen: boolean = false;

    @state()
    walkthroughDismissed: boolean = false;

    private point_id?: string;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);
    private readonly bindTranslationState = new StateController(this, translationData);

    @state()
    renderSpreadsheet: boolean = false;

    @state()
    renderInsights: boolean = false;

    unloadEventListener!: any;

    async firstUpdated() {
        window.addEventListener('resize', this.handleWindowSizing.bind(this));
        await this.fixVaadinFocusTrap();
        await this.startup();
    }

    disconnectedCallback() {
        window.removeEventListener('beforeunload', this.unloadEventListener, false);
        super.disconnectedCallback();
    }

    private async grantAccessClick() {
        logWithAmplitude(this.amplitude_callsite, 'Grant Access Click');
        await topicData.mapComponent?.manageAccessClick();
    }

    private async startup() {
        const queryString = getQueryStringParameters();
        const point_id = queryString.get('point_id');

        this.unloadEventListener = this._handleUnload.bind(this);
        window.addEventListener('beforeunload', this.unloadEventListener, false);

        this.point_id = point_id;

        // Check if walkthrough was dismissed
        const dismissed = getFromLocalStorage('walkthrough-dismissed');
        if (dismissed === 'true') {
            this.walkthroughDismissed = true;
        }

        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            false,
            false,
        );
        topicData.mapComponent = this.mapComponentWrapper;

        // If an admin gives this link to someone without write access, then they should get redirected.
        if (!topicData.isWriter) {
            const message = `It looks like you have an edit link but we haven't logged you in yet! You can do that here.`;
            window.location.replace(
                `/#login?callback_url=${encodeURIComponent(window.location.href)}&message=${encodeURIComponent(message)}`
            );
            return;
        }
        window.dispatchEvent(new CustomEvent('page-content-ready'));

        if (isDefined(topicData.topic)) {
            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                false,
                true,
                challengeData.challenge ? challengeData.allCheckins : accountInfo.account_checkins
            );

            await translationData.instantiateTranslations(
                true
            );

            logWithAmplitude(this.amplitude_callsite,'Load Topic Statistics', {
                topic_id: topicData.topic._id.$oid,
                topic_views: topicData.topic.statistics.approximate_topic_views,
                topic_views_seven_days: topicData.topic.statistics.approximate_topic_seven_day_views,
                topic_views_thirty_days: topicData.topic.statistics.approximate_topic_thirty_day_views,
                topic_views_fourtneen_days: topicData.topic.statistics.approximate_topic_fourteen_day_views,
                num_points: topicData.topic.statistics.approximate_point_count,
                point_views: topicData.topic.statistics.approximate_point_views
            });

            // Ensure that local storage, amplitude, and chameleon have an email if possible
            // Because we are constantly adding new tools that require login, we should always run this to make sure all are updated
            const local_storage_email = getFromLocalStorage('email');
            const topic_email = topicData.email;
            if (accountInfo.isAuthenticated) {
                saveEmail(accountInfo.email);
            } else if (local_storage_email !== null) {
                saveEmail(local_storage_email);
            } else if (topic_email !== undefined) {
                saveEmail(topic_email);
            }
            this.conditionallyOpenOnboarding();
            this.openIntercomForLeads(getFromLocalStorage('email') ?? topicData.email);
            try {
                setIntercomUserDetails();
            } catch (e) {
                console.error(e);
            }

            // Set the OpenGraph data dynamically.
            // This is not strictly required, since the redirector currently handles this for bots on the server side.
            (document as any).querySelector('meta[property="og:title"]').content = topicData.name;

            if (topicData.topic?.creator) {
                (document as any).querySelector(
                    'meta[property="og:description"]'
                ).content = `Community map made by ${topicData.topic?.creator} just for you. Check it out!`;
            }
        }

        if (!organizationInfo.isGuest && topicData.isWriter) {
            try {
                this.allTopicUsers = await this.getTopicUsers();
            } catch (e) {
                console.error(e);
            }
        }
    }

    private async closeOnboardingModal(e: CustomEvent) {
        this.onboardingModal.close();
        // hack to refresh topic data in map view
        await this.mapComponentWrapper?.refreshMap();
        try {
            await this.mapHeader.refreshText();
        } catch (e) {
            console.error(e);
        }
        show_confetti();

        if (!detectMobileViewport()) {
            await this.showNewUserTooltips();
        }
    }

    private async openOnboardingModal() {
        this.onboardingModal.open = true;
    }

    private async updateUserPreviewStatus() {
        this.userPreview = !this.userPreview;
        if (!this.userPreview) {
            this.userPreviewMode = PreviewType.Desktop;
            this.mobilePreview = false;
        }
    }

    private async updateUserPreviewMode(e: CustomEvent | any) {
        this.userPreviewMode = e.detail.previewMode;
        this.mobilePreview = e.detail.previewMode === PreviewType.Mobile && this.userPreview;
        if (topicData.topic?.cc_read_link) {
            try {this.qrCode = await getQRCode(this.amplitude_callsite, topicData.topic?.cc_read_link);
            }
            catch (e) {
                console.error(e);
            }
        }
    }

    private conditionallyOpenOnboarding() {
        if (topicData.topic === undefined || this.onboardingModal.open) {
            return;
        }

        const inInitialView =
            (topicData.topic.default_map_center.search === 'default_map_center' || topicData.topic?.name.includes('from Template') || (topicData.topic?.creator ?? '').includes('from Template')) &&
            !pointCache.hasPoints;

        if (inInitialView) {
            // Check if an intent was pre-selected via query param (e.g. from the new app)
            const intentParam = new URLSearchParams(window.location.search).get('intent');
            const intentMap: Record<string, IntentType> = {
                map: IntentType.Map,
                guide: IntentType.Guide,
                passport: IntentType.Passport,
                hunt: IntentType.Hunt,
                event: IntentType.Event,
                bestof: IntentType.BestOf,
            };
            const preselectedIntent = intentParam ? intentMap[intentParam.toLowerCase()] : undefined;

            if (preselectedIntent !== undefined) {
                // Skip the Intent step — go straight to Prompt
                topicData.intent = preselectedIntent;
                this.onboardingStep = OnboardingState.Prompt;
                this.onboardingModalComponent.selectedIntent = preselectedIntent;
                // Install addons for the selected intent (same as goToNextStep from Intent)
                this.onboardingModalComponent.addAppropriateIntentChanges(preselectedIntent).catch(e => {
                    console.error('Error adding intent changes: ', e);
                });
            } else {
                this.onboardingStep = OnboardingState.Intent;
            }
        }

        if (this.onboardingStep === OnboardingState.Points) {
            topicData.refreshBounds();
        }

        // if user is editor, not owner, or had a subscription, or is legacy, let them through - otherwise, show upgrade or subscribe
        // add once user types are updated

        if (inInitialView || isDefined(this.onboardingStep)) {
            this.onboardingModal.open = true;
        }
    }

    private openIntercomForLeads(email: string) {
        if (topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations) {
            return;
        }
        // if email doesn't include gmail, hotmail, yahoo, or outlook, then open intercom automatically
        if (
            !email.includes('gmail') &&
            !email.includes('hotmail') &&
            !email.includes('yahoo') &&
            !email.includes('outlook') &&
            !email.includes('icloud') &&
            !email.includes('aol') &&
            !email.includes('protonmail') &&
            !email.includes('zoho') &&
            !email.includes('yandex') &&
            !email.includes('mail')
        ) {
            logAsLead(this.amplitude_callsite, email);
        }
    }

    _handleUnload(e: BeforeUnloadEvent) {
        if (this.renderSpreadsheet) {
            e.preventDefault();

            // this wont be shown in chrome - they dont allow this text to be customized
            e.returnValue =
                'Are you sure you want to leave spreadsheet view? Your changes will not be saved.';
            window.prompt(e.returnValue);
            return e;
        }
    }

    openDemoVideo() {
        const link = 'https://www.proxi.co/proxi-demo-video';
        window.open(link, '_blank');
    }

    private async fixVaadinFocusTrap() {
        await removeInertFromVaadinOverlay('VAADIN-CONFIRM-DIALOG-OVERLAY');
        const body = document.querySelector('body');

        if (body) {
            const observer = new MutationObserver((mutationRecords, _) => {
                const confirmDialog = mutationRecords
                    .map((m) => {
                        let element;
                        m.addedNodes.forEach((n: any) => {
                            if (n.tagName === 'VAADIN-CONFIRM-DIALOG-OVERLAY') {
                                element = n;
                            }
                        });

                        return element;
                    })
                    .filter((e) => e !== undefined)[0];

                if (confirmDialog !== undefined) {
                    (confirmDialog as any).focusTrap = false;
                }
            });

            observer.observe(body, { childList: true });
        }
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
        this.handleWindowSizing();
    }

    private handleWindowSizing() {
        if (this.activePanel !== ActivePanel.None && window.innerWidth <= 450) {
            // A modal is open and we are on mobile, so hide the map
            this.showMapComponent = false;
        } else {
            this.showMapComponent = true;
        }
    }

    private openUpgradeDialog(e: CustomEvent | any) {
        if (isDefined(this.upgradeDialogComponent)) {
            this.upgradeDialogComponent.open = true;
            // Anything not in Pro is a Premium feature — upsell the Premium bundle.
            if (!FeatureTierLevelMap[e.detail.featureType].includes(FeatureTierLevel.ProBase)) {
                this.upgradeDialogInnerComponent.addon = StandardProAddOns.HaveItAll;
            }
            this.upgradeDialogInnerComponent.featureType = e.detail.featureType;
            // listen for the close event
            this.upgradeDialogComponent.addEventListener('close', () => {
                this.upgradeDialogComponent?.close();
            });
        }
    }

    private async openCustomInsightsModal(e: CustomEvent | any) { // this is a very expensive call to load the UI bakery info.  ensure that it isn't loading unless clicked.
        if (featuresInfo.advancedInsightsEnabled || topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations) {
            this.showAdvancedInsightsModal = true;
            await this.updateComplete;
            if (isDefined(this.customAdvancedInsightsModal)) {
                this.customAdvancedInsightsModal.open = true;
            }
            logWithAmplitude(this.amplitude_callsite, `Advanced Insights Load`, {
                topic_id: topicData.topicId,
            });
        } else {
            this.openUpgradeDialog({ detail: { featureType: FeatureType.AdvancedInsights } });
            if (isDefined(this.upgradeDialogComponent)) {
            this.upgradeDialogComponent.addEventListener('close', () => {
                this.upgradeDialogComponent?.close();
            });}
        }

        logWithAmplitude(this.amplitude_callsite, `Advanced Insights Click`, {
            topic_id: topicData.topicId,
        });
    }

    async enableEditing() {
        this.isEditing = true;
        // Optionally, focus the input after rendering
        await this.updateComplete.then(() => {
            const input = this.shadowRoot?.querySelector('input');
            input?.focus();
        });
    }

    async updateTopicName(event: Event) {
        const input = event.target as HTMLInputElement;
        await topicData.fetchAndUpdateTopicData({ name: input.value }, this.amplitude_callsite);
        this.isEditing = false;
        await this.mapHeader.refreshText();

    }

    async handleKeydown(event: KeyboardEvent) {
        if (event.key === 'Enter') {
            await this.updateTopicName(event);
        } else if (event.key === 'Escape') {
            this.isEditing = false;
        }
    }

    async getTopicUsers() {
        const organization_members: UserInfo[] = organizationInfo.allMembers.filter((member) => member.role.OrganizationRoleType.level !== OrganizationPermissionLevel.LIMITED).map((member) => {
            return {
                email: member.email,
                name: member.name,
                avatar: member.avatar,
            };
        });
        const topic_members: UserInfo[] = (await topicData.getTopicRoles()).map((member) => {
            return {
                email: member.email,
                name: undefined,
                avatar: undefined,
            };
        }
        );

        // add the organization members to the topic members and remove duplicates
        const all_members = topic_members.concat(organization_members);
        const unique_members = all_members.filter((member, index, self) =>
            index === self.findIndex((t) => (
                t.email.toLowerCase() === member.email.toLowerCase()
            ))
        );

        return unique_members;
    }

    mapDrawerChanged(event: CustomEvent) {
        this.requestUpdate();
    }

    render() {
        let logo = renderOrganizationLogo(organizationInfo.organization?.logo);
        if (topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations) {
            const whlogo = `https://whrelocations.com/wp-content/uploads/2021/11/1417748_541747545906657_117082684_o.jpg`;
            logo = renderOrganizationLogo(whlogo);
        }
        return html`
            <div id="rootContainer">
                ${topicData.isReady
                    ? html`
                        <div id="body" class="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations ? 'full-height-override' : ''}">
                                    <div id="drawerMain" class="${this.userPreview ? 'preview-hidden' : ''} ${this.isMini ? 'mini' : ''}">
                                        ${!this.isMini ? html`
                                            <div style="display: flex; justify-content: center; align-items: center; cursor: pointer" @click=${this.proxiBackpackClick}>
                                                ${organizationInfo.organization?.logo === 'https://map.proxi.co/images/logo.png' ? renderProxiLogo : logo}
                                            </div>
                                            <div class="map-name-section">
                                            ${this.isEditing && !this.isMini
                                                ? html`
                                                    <input
                                                        type="text"
                                                        .value="${topicData.name}"
                                                        @blur="${this.updateTopicName}"
                                                        @keydown="${this.handleKeydown}"
                                                    />
                                                `
                                                : html`
                                                    <span
                                                        @click="${this.enableEditing}"
                                                        style="cursor: text;"
                                                    >
                                                        <b>${topicData.name}</b>
                                                    </span>
                                                `
                                            }
                                                ${topicData.topic?.updated_at ? html`<span class="stats-subtext"> Updated ${getRelativeTime(topicData.topic?.updated_at)}</span>` : ''}
                                            </div>
                                        ` : html`
                                            <account-avatar
                                                .imageUrl=${accountInfo.account?.image_url}
                                                size="sm"
                                                style="margin: 1rem auto; cursor: pointer"
                                                @click=${this.proxiBackpackClick}
                                                .backgroundColor=${organizationInfo.organization?.primary_color}
                                                .textColor=${organizationInfo.organization?.secondary_color}
                                                .isTeam=${isDefined(organizationInfo.organization) && organizationInfo.organization?.is_collaborator}
                                            >
                                                ${getInitials(
                                                    organizationInfo.organization?.name ?? accountInfo.fullName,
                                                    accountInfo.email
                                                )}
                                            </account-avatar>
                                        `}
                                        ${organizationInfo.organization?.is_collaborator === true ? html`
                                            <div class="people-on-map">
                                                <multi-user-avatar
                                                    @click=${this.grantAccessClick}
                                                    class="desktopOnly"
                                                    .backgroundColor=${organizationInfo.organization?.secondary_color}
                                                    .textColor=${organizationInfo.organization?.primary_color}
                                                    .users=${this.allTopicUsers}
                                                    size="sm"
                                                    .maxAvatars=${!this.isMini ? 5 : 1}
                                                >
                                                </multi-user-avatar>
                                            </div>
                                            `
                                        : ''}

                                        <div class="drawer-buttons">
                                            <drawer-button-component
                                                icon="palette"
                                                class="customize-border"
                                                id="style"
                                                .active="${this.activePanel === ActivePanel.MapStyle}"
                                                .action="${this.panelButtonClicked}"
                                                label="Brand &#38; Style"
                                                panel="${ActivePanel.MapStyle}"
                                                @on-button-click="${this.drawerButtonClicked}"
                                                .mini="${this.isMini}"
                                                ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                            >
                                            </drawer-button-component>
                                            <drawer-button-component
                                                icon="category"
                                                class="customize-border"
                                                id="category"
                                                .active="${this.activePanel === ActivePanel.CategoriesTags}"
                                                .action="${this.panelButtonClicked}"
                                                label="Categories &#38; Tags"
                                                panel="${ActivePanel.CategoriesTags}"
                                                @on-button-click="${this.drawerButtonClicked}"
                                                .mini="${this.isMini}"
                                            >
                                            </drawer-button-component>
                                            <drawer-button-component
                                                icon="settings"
                                                label="Settings"
                                                class="customize-border "
                                                id="advanced-features"
                                                .mini="${this.isMini}"
                                                hasChild
                                                ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                            >
                                                <drawer-button-component
                                                    icon="edit_location_alt"
                                                    label="Point Settings"
                                                    class="customize-border "
                                                    .active="${this.activePanel === ActivePanel.PointSettings}"
                                                    id="advanced-features"
                                                    .action="${this.panelButtonClicked}"
                                                    panel="${ActivePanel.PointSettings}"
                                                    @on-button-click="${this.drawerButtonClicked}"
                                                    .mini="${this.isMini}"
                                                    isChild
                                                    ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                                >
                                                </drawer-button-component>
                                                <drawer-button-component
                                                    icon="map"
                                                    label="Map Settings"
                                                    class="customize-border "
                                                    .active="${this.activePanel === ActivePanel.MapSettings}"
                                                    id="advanced-features"
                                                    .action="${this.panelButtonClicked}"
                                                    panel="${ActivePanel.MapSettings}"
                                                    @on-button-click="${this.drawerButtonClicked}"
                                                    .mini="${this.isMini}"
                                                    isChild
                                                    ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                                >
                                                </drawer-button-component>

                                                <drawer-button-component
                                                    icon="group_work"
                                                    label="Icon Settings"
                                                    class="customize-border "
                                                    .active="${this.activePanel === ActivePanel.IconSettings}"
                                                    id="advanced-features"
                                                    .action="${this.panelButtonClicked}"
                                                    panel="${ActivePanel.IconSettings}"
                                                    @on-button-click="${this.drawerButtonClicked}"
                                                    .mini="${this.isMini}"
                                                    isChild
                                                    ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                                >
                                                </drawer-button-component>
                                                <drawer-button-component
                                                    icon="app_shortcut"
                                                    label="Share Settings"
                                                    class="customize-border "
                                                    .active="${this.activePanel === ActivePanel.ShareSettings}"
                                                    id="advanced-features"
                                                    .action="${this.panelButtonClicked}"
                                                    panel="${ActivePanel.ShareSettings}"
                                                    @on-button-click="${this.drawerButtonClicked}"
                                                    .mini="${this.isMini}"
                                                    isChild
                                                    ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                                >
                                                </drawer-button-component>
                                            </drawer-button-component>
                                            <drawer-button-component
                                                icon="list"
                                                label="Map Points"
                                                class="data-border"
                                                .active="${this.activePanel === ActivePanel.PointsList}"
                                                id="list-of-points"
                                                .action="${this.panelButtonClicked}"
                                                panel="${ActivePanel.PointsList}"
                                                .mini="${this.isMini}"
                                                @on-button-click="${this.drawerButtonClicked}"

                                            >
                                            </drawer-button-component>
                                            <drawer-button-component
                                                icon="table_chart"
                                                label="Spreadsheet View"
                                                class="data-border"
                                                .active="${this.renderSpreadsheet}"
                                                id="spreadsheet-view"
                                                ?hideOnMobile=${true}
                                                @on-button-click=${this.openSpreadsheetView}
                                                .mini="${this.isMini}"
                                                ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                            >
                                            </drawer-button-component>
                                            <drawer-button-component
                                                icon="psychology"
                                                label="AI Point Generator"
                                                class="data-border"
                                                .active="${this.activePanel === ActivePanel.AiPointGeneration}"
                                                id="list-of-points"
                                                .action="${this.panelButtonClicked}"
                                                panel="${ActivePanel.AiPointGeneration}"
                                                .mini="${this.isMini}"
                                                @on-button-click="${this.drawerButtonClicked}"

                                            >
                                            </drawer-button-component>
                                            <drawer-button-component
                                                icon="query_stats"
                                                label="Insights"
                                                class="data-border"
                                                .active="${this.renderInsights}"
                                                id="insights"
                                                ?hideOnMobile=${true}
                                                @on-button-click=${this.openInsights}
                                                .mini="${this.isMini}"
                                                .available=${featuresInfo.advancedInsightsEnabled}
                                                ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                            >
                                            </drawer-button-component>
                                            <drawer-button-component
                                                icon="apps"
                                                class="add-on-border"
                                                id="add-ons"
                                                .action="${this.panelButtonClicked}"
                                                .active="${this.activePanel === ActivePanel.AddOns}"
                                                panel="${ActivePanel.AddOns}"
                                                @on-button-click="${this.drawerButtonClicked}"
                                                .mini="${this.isMini}"
                                                ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                                label="Apps"
                                                >
                                            </drawer-button-component>
                                            ${topicData.activatedAddons.map((addon) => {
                                                return html`
                                                    <drawer-button-component
                                                        icon="${addon.icon_path}"
                                                        label="${addon.title}"
                                                        class="add-on-border"
                                                        id="${addon.title}"
                                                        .action="${this.panelButtonClicked}"
                                                        .active="${this.activeAddOn?._id === addon._id}"
                                                        .panel="${this.getAddOnPanel(addon.component)}"
                                                        @on-button-click="${this.drawerButtonClicked}"
                                                        .mini="${this.isMini}"
                                                        .isAddOn="${true}"
                                                        ?hidden="${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations || organizationInfo.isGuest}"
                                                    >
                                                    </drawer-button-component>
                                                `;
                                            } )}
                                        </div>
                                        <div class="drawer-footer ${topicData.hasExceptions === IntegrationException.WelcomeHomeRelocations ? 'hidden' : ''}">
                                            ${isDefined(topicData.intent) && !this.walkthroughDismissed && !this.isMini  ? html`
                                                 <div class="walkthrough-section">
                                                    <div class="walkthrough-content">
                                                        <h4 class="walkthrough-title">💡 Want the Best Experience?</h4>
                                                        <p class="walkthrough-description">
                                                            For ${topicData.getIntentTitle(topicData.intent)}, we recommend learning more about the features to get the most out of your map.
                                                        </p>
                                                        <div class="walkthrough-links">
                                                            <a
                                                                href="${topicData.getSetupVideoLink(topicData.intent)}"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                class="walkthrough-link">
                                                                Watch ${topicData.getIntentTitle(topicData.intent)} Setup Video 🔗
                                                            </a>
                                                            <a
                                                                href="https://app.proxi.co/demo"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                class="walkthrough-link">
                                                                Schedule a Demo/Walkthrough 🔗
                                                            </a>
                                                        </div>
                                                    </div>
                                                    <mwc-icon class="walkthrough-close" @click="${this.dismissWalkthrough}">close</mwc-icon>
                                                </div>
                                            ` : ''}
                                            <div class="helpSection ${!this.isMini ? '' : 'miniHelp'}">
                                                <span class="">
                                                    <help-menu
                                                        id="helpmenu"
                                                        .mini="${this.isMini}"
                                                        @menu-opened="${this.closePanel}"
                                                        type="backpack">
                                                        ${!this.isMini ? 'help' : ''}
                                                    </help-menu>
                                                </span>
                                                <span class="helpOption" @click="${this.openDemoVideo}">
                                                    <mwc-icon>smart_display</mwc-icon>
                                                    ${!this.isMini ? 'Demo Video' : ''}
                                                </span>
                                                <span class="helpOption" @click=${this.learnClick}>
                                                    <mwc-icon>description</mwc-icon>
                                                    ${!this.isMini ? 'Docs' : ''}
                                                </span>
                                            </div>
                                            ${accountInfo.isAuthenticated ? html`
                                            <context-menu mini menu_only id="accountMenu" class="account-menu">
                                                <div class="user-area" @click=${() => {this.closePanel(); this.accountMenu.cornerMenu.open = true;}}>
                                                    <account-avatar
                                                        .imageUrl=${accountInfo.account?.image_url}
                                                        size="sm">
                                                        ${getInitials(
                                                            accountInfo.fullName,
                                                            accountInfo.email
                                                        )}
                                                    </account-avatar>
                                                    ${!this.isMini ? html`
                                                        <div>
                                                            <b>${undefinedIfEmpty(accountInfo.fullName) ?? accountInfo.email}</b>
                                                        </div>
                                                    ` : ''}
                                                </div>
                                            </context-menu>
                                            ` : html`
                                                <div
                                                    class="log-in-area"
                                                    @click="${this.signInClick}"
                                                >
                                                    <mwc-icon class="leftIcon">person_outline</mwc-icon>
                                                    ${!this.isMini ? html`<span class="linkText">Log in to save and manage your maps</span>` : ''}
                                                </div>
                                            `}
                                        </div>
                                    </div>
                            <div class="drawerPanel ${this.activePanel === ActivePanel.None || this.fullWidthPanelOpen ? 'closed' : 'opened'} ${this.userPreview ? 'preview-hidden' : ''}">
                                ${this.activePanel !== ActivePanel.None && !this.fullWidthPanelOpen ? html`
                                    ${this.renderDrawerPanel()}
                                ` : ''}
                            </div>
                            <div class="main-container ${this.userPreview ? 'preview-darker-background' : ''} ${this.activePanel === ActivePanel.None ? '' : 'opened'} ${this.fullWidthPanelOpen ? 'full-width' : ''}">
                                ${this.renderSpreadsheet || this.renderInsights || this.fullWidthPanelOpen ? '' : html`
                                    <map-header-buttons
                                        id="mapHeaderButtons"
                                        .userPreview="${this.userPreview}"
                                        @open-custom-insights-modal="${this.openCustomInsightsModal}"
                                        @set_preview_status="${this.updateUserPreviewStatus}"
                                        @set_preview_mode="${this.updateUserPreviewMode}"
                                        >
                                    </map-header-buttons>
                                `}
                                <div class="main-section-container
                                    ${this.userPreview ?
                                        this.userPreviewMode === PreviewType.Desktop ? 'preview-desktop' : 'preview-mobile'
                                         : ''}">
                                        ${this.fullWidthPanelOpen ? '' : html`
                                            <map-header id="map-header">
                                            <div style="display: flex; justify-content: flex-end">
                                            ${this.renderSpreadsheet || this.renderInsights || this.fullWidthPanelOpen ? '' : html`
                                            ${topicData.topic?.translation_languages && topicData.topic?.translation_languages.length > 0 ? html`
                                                <language-selector></language-selector>
                                            ` : ''}
                                            ${this.userPreview ? ''
                                                : html`
                                                    <div class="nav-content">
                                                        ${topicData.hasExceptions === IntegrationException.TheGoSolution || topicData.hasExceptions === IntegrationException.Telco || topicData.hasExceptions === IntegrationException.DefHero ? html`
                                                            <proxi-autocomplete style="max-height: 40px;width: 300px;z-index: 4;"></proxi-autocomplete>
                                                        ` : ''}
                                                        ${topicData.topic?.hide_map_info_drawer !== true && !detectMobileViewport(this.userPreview) ? html`
                                                            <mwc-icon
                                                                style="cursor: pointer;"
                                                                @click=${async () => await this.mapComponentWrapper?.setMapDrawer(MapDrawer.MapInfo)}
                                                                >manage_search</mwc-icon>
                                                        ` : ''}
                                                    </div>
                                                    `}
                                                `}
                                                </div>
                                            </map-header>
                                            `}
                                    ${this.renderSpreadsheet
                                        ? html` <hot-points
                                                id="spreadsheet"
                                                @on-save="${this.onSpreadsheetSave}"
                                                @on-close="${this.closeSpreadsheet}"
                                            >
                                            </hot-points>` :
                                            this.renderInsights ? html`
                                                <div id="insightsArea">
                                                    <mwc-icon class="insights-close" @click="${this.closeInsights}">close</mwc-icon>
                                                    <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; gap: 16px;">
                                                        <p style="font-size: 16px; color: #666;">Analytics have moved to the new dashboard.</p>
                                                        <a href="${getNewUiUrl()}/maps/${topicData.topicId}/activity"
                                                           target="_blank"
                                                           rel="noopener noreferrer"
                                                           style="padding: 12px 24px; background: #2563eb; color: white; border-radius: 8px; text-decoration: none; font-weight: 500;">
                                                            View Analytics in New Dashboard
                                                        </a>
                                                    </div>
                                                </div>`
                                        : this.fullWidthPanelOpen && this.activeAddOn ?  html`
                                            <div style="flex: 1;display: flex;width: 100%;flex-direction: column;height: calc(100vh - 100px);">
                                                ${this.renderAddonInnards(this.activeAddOn)}
                                            </div>
                                        `
                                        :html`
                                            <div class="map__wrapper">
                                                ${this.showMapComponent
                                                    ? html`<map-component
                                                        id="mapComponentWrapper"
                                                        .showAddPointButton=${true}
                                                        .focused_point_id="${this.point_id}"
                                                        .isUserPreview="${this.userPreview}"
                                                        .mobilePreview="${this.mobilePreview}"
                                                        .election="${topicData.electionHappeningNow}"
                                                        .showOtherDrawerButtons="${false}"
                                                        hideEmailCapturePopUp="${true}"
                                                        .showElectionLeaders="${electionResultsAreLive(topicData.electionHappeningNow)}"
                                                        isAdminPortalDesktop = '${true}'
                                                        @open-upgrade-dialog=${this.openUpgradeDialog}
                                                        @drawer-changed="${this.mapDrawerChanged}"
                                                    >
                                                    </map-component>
                                                    `
                                                    : ''}
                                            </div>
                                        `}
                                    </div>
                                    ${this.userPreview && this.mobilePreview ? html`
                                        <div class="qr_code__wrapper">
                                            <p> The mobile preview is an approximation of how your map could look on mobile. </p>
                                            <p> Scan the QR code below to view your live map on your phone. </p>
                                            <img .src="${this.qrCode}" alt="QR Code for mobile preview" />
                                        </div>
                                    ` : ''}

                                </div>
                            </div>
                            ${topicData.isReady ? html`
                                <mwc-dialog
                                    id="onboardingModal"
                                    hideActions
                                    scrimClickAction=""
                                    escapeKeyAction=""
                                >
                                    <modal-dialog
                                        size="lg"
                                        @close="${this.closeOnboardingModal}"
                                        ?hideclosebutton="${topicData.email === undefined || topicData.email === ''}"
                                    >
                                        <onboarding-modal
                                            id="onboardingModalComponent"
                                            slot="body"
                                            .onboardingStep=${this.onboardingStep}
                                            @openSignIn="${this.signInClick}"
                                            @on-close-onboarding-modal="${this.closeOnboardingModal}"
                                            @on-open-onboarding-modal="${this.openOnboardingModal}"
                                            @on-open-upgrade-dialog="${this.openUpgradeDialog}"
                                        >
                                        </onboarding-modal>
                                    </modal-dialog>
                                </mwc-dialog>
                                <mwc-dialog id="signinModalComponent" class="dialog" hideActions>
                                    <modal-dialog
                                        size="md"
                                        @close="${() => this.signinModalComponent.close()}"
                                    >
                                        <signin-modal-component
                                            slot="body"
                                            @close="${() => this.signinModalComponent.close()}"
                                        >
                                        </signin-modal-component>
                                    </modal-dialog>
                                </mwc-dialog>
                            `: ''}
                            <mwc-dialog id="closeSpreadsheetModalDialog" class="dialog" hideActions>
                                <modal-dialog
                                    size="lg"
                                    @close="${() => this.closeSpreadsheetModalDialog.close()}"
                                >
                                    <leave-spreadsheet-modal
                                        slot="body"
                                        id="closeSpreadsheetModal"
                                        @closeModal="${this.removeCloseSpreadsheetModal}"
                                        @closeSpreadsheet="${this.handleCloseSpreadsheet}"
                                    >
                                    </leave-spreadsheet-modal>
                                </modal-dialog>
                            </mwc-dialog>
                            ${this.showAdvancedInsightsModal
                                ? html`<mwc-dialog
                                        id="customAdvancedInsightsModal"
                                        class="dialog"
                                        hideActions
                                    >
                                        <modal-dialog
                                            size="xl"
                                            @close="${() => this.customAdvancedInsightsModal?.close()}"
                                        >
                                            <div slot="body" style="display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 48px; gap: 16px;">
                                                <p style="font-size: 16px; color: #666;">Analytics have moved to the new dashboard.</p>
                                                <a href="${getNewUiUrl()}/maps/${topicData.topicId}/activity"
                                                   target="_blank"
                                                   rel="noopener noreferrer"
                                                   style="padding: 12px 24px; background: #2563eb; color: white; border-radius: 8px; text-decoration: none; font-weight: 500;">
                                                    View Analytics in New Dashboard
                                                </a>
                                            </div>
                                        </modal-dialog>
                                    </mwc-dialog>`
                                : ''}
                            <mwc-dialog id="upgradeDialogComponent" class="dialog" hideActions>
                                <modal-dialog
                                    size="md"
                                    @close="${() => this.upgradeDialogComponent?.close()}"
                                >
                                    <upgrade-dialog
                                        id="upgradeDialogInnerComponent"
                                        slot="body"
                                        .featureType="${FeatureType.PrintableMap}"
                                        @close="${() => this.upgradeDialogComponent?.close()}"
                                    >
                                    </upgrade-dialog>
                                </modal-dialog>
                            </mwc-dialog>
                        </div>` : ''}
                <error-toast id="errorToast"></error-toast>
            </div>
        `;
    }

    private getAddOnPanel(addon: AddOnType) {
        switch (addon) {
            case AddOnType.Challenge:
                return ActivePanel.Challenge;
            case AddOnType.ScavengerHunt:
                return ActivePanel.ScavengerHunt;
            case AddOnType.Zapier:
                return ActivePanel.ZapierIntegration;
            case AddOnType.LeadPopUp:
                return ActivePanel.PopUpEmailCapture;
            case AddOnType.Password:
                return ActivePanel.Passwords;
            case AddOnType.EmailGate:
                return ActivePanel.EmailGate;
            case AddOnType.Translation:
                return ActivePanel.Translation;
            case AddOnType.CustomForm:
                return ActivePanel.CollabSettings;
            case AddOnType.BestOf:
                return ActivePanel.Election;
            case AddOnType.BlogToMapAI:
                return ActivePanel.BlogToMapAI;
            case AddOnType.Paywall:
                return ActivePanel.Paywalls;
            case AddOnType.LeadPointForm:
                return ActivePanel.PointEmailCapture;
            case AddOnType.KmlUpload:
                return ActivePanel.KmlUpload;
            case AddOnType.Comments:
                return ActivePanel.Comments;
            case AddOnType.IntegrationAutomation:
                return ActivePanel.IntegrationAutomation;
            case AddOnType.Guide:
                return ActivePanel.Guide;
            case AddOnType.TreasureHunt:
                return ActivePanel.TreasureHunt;
            default:
                return ActivePanel.None;
        }
    }

    // TODO add spreadsheet and insights here and restructure how those are handled to follow a pattern
    fullWidthPanels =
        [ActivePanel.Challenge,
            ActivePanel.PopUpEmailCapture,
            ActivePanel.TreasureHunt,
            ActivePanel.Guide,
            ActivePanel.ScavengerHunt,
            ActivePanel.Election,
            ActivePanel.CollabSettings,
            ActivePanel.Comments,
            ActivePanel.IntegrationAutomation,
            ActivePanel.Paywalls];

    async closeFragment() {
        await this.panelButtonClicked(this.activePanel);
    }

    renderAddonInnards(addon: GetAddOnResponse) {
        switch (this.activeAddOn?.component) {
            case AddOnType.Challenge:
                return html`
                    <admin-challenge-addon
                        class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-challenge-addon>
                `;
            case AddOnType.Guide:
                return html`
                    <admin-guide-addon
                        class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-guide-addon>
                `;
            case AddOnType.TreasureHunt:
                return html`
                    <admin-treasure-hunt-addon
                        class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-treasure-hunt-addon>
                `;
            case AddOnType.ScavengerHunt:
                return html`
                    <admin-hunt-addon
                        class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-hunt-addon>
                `;
            case AddOnType.BestOf:
                return html`
                    <admin-election-addon
                        class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-election-addon>
                `;
            case AddOnType.CustomForm:
                return html` <admin-collab-settings-addon
                class="inner-addon"
                    @close-fragment="${this.closeFragment}"
                    @open-upgrade-dialog=${this.openUpgradeDialog}>
                </admin-collab-settings-addon> `;
            case AddOnType.Comments:
                return html` <admin-comment-addon
                class="inner-addon"
                    @close-fragment="${this.closeFragment}"
                    @open-upgrade-dialog=${this.openUpgradeDialog}>
                </admin-comment-addon> `;
            case AddOnType.IntegrationAutomation:
                return html` <admin-integration-automation-addon
                class="inner-addon"
                    @close-fragment="${this.closeFragment}"
                    @open-upgrade-dialog=${this.openUpgradeDialog}>
                </admin-integration-automation-addon> `;
            case AddOnType.BlogToMapAI:
                return html` <admin-blog-to-map-ai-addon
                class="inner-addon"
                    @close-fragment="${this.closeFragment}"
                    @open-upgrade-dialog=${this.openUpgradeDialog}>
            </admin-blog-to-map-ai-addon> `;
            case AddOnType.Translation:
                return html` <admin-translation-addon
                class="inner-addon"
                    @close-fragment="${this.closeFragment}"
                    @open-upgrade-dialog=${this.openUpgradeDialog}>
            </admin-translation-addon> `;
            case AddOnType.LeadPopUp:
                return html`
                    <admin-popup-addon
                    class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-popup-addon>
                `;
            case AddOnType.LeadPointForm :
                return html`
                    <admin-capture-emails-addon
                    class="inner-addon"
                        style="width: 100%;"
                        .selected_tab=${'Point Form'}
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-capture-emails-addon>
                `;
            case AddOnType.Password:
                return html`
                    <admin-gated-access-addon
                    class="inner-addon"
                        .selectedTab=${'password'}
                        @close-fragment="${this.closeFragment}"
                        @on-preview="${this.updateUserPreviewStatus}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-gated-access-addon>
                `;
            case AddOnType.EmailGate:
                return html`
                    <admin-gated-access-addon
                    class="inner-addon"
                        .selectedTab=${'email'}
                        @close-fragment="${this.closeFragment}"
                        @on-preview="${this.updateUserPreviewStatus}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-gated-access-addon>
                `;
            case AddOnType.Paywall:
                return html`
                    <admin-paywall-addon
                    class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-paywall-addon>
                `;
            case AddOnType.Zapier:
                return html` <admin-zapier-integration-addon
                class="inner-addon"

                    @close-fragment="${this.closeFragment}"
                    @open-upgrade-dialog=${this.openUpgradeDialog}>
            </admin-zapier-integration-addon> `;
            case AddOnType.KmlUpload:
                return html` <kml-upload-component
                class="inner-addon"
                    @close-fragment="${this.closeFragment}"
                    @open-upgrade-dialog=${this.openUpgradeDialog}>
                </kml-upload-component> `;
        }
    }


    renderDrawerPanel() {
        if (this.activeAddOn) {
            return html`
                <admin-fragment-holder
                    title=${this.activeAddOn.title}
                    description=${this.activeAddOn.subtitle}
                    .addOn=${this.activeAddOn}
                    @close-fragment=${this.closeFragment}
                    >
                    ${this.renderAddonInnards(this.activeAddOn)}

                </admin-fragment-holder>
                    `;
        }


        switch (this.activePanel) {
            case ActivePanel.MapStyle:
                return html`
                    <admin-fragment-holder
                        title="Map Style"
                        description="Customize the look and feel of your map."
                        @close-fragment=${this.closeFragment}
                        >
                        <admin-style-settings-fragment
                        class="inner-addon"
                            @close-fragment="${this.closeFragment}"
                            @open-upgrade-dialog=${this.openUpgradeDialog}
                        >
                        </admin-style-settings-fragment>
                    </admin-fragment-holder>
                `;
            case ActivePanel.CategoriesTags:
                return html`
                    <admin-fragment-holder
                        title="Category and Tag Management"
                        description="This section contains the filters for your map. Customize the categories and tags to help your viewers find what they need."
                        .addOn=${this.activeAddOn}
                        @close-fragment=${this.closeFragment}
                        >
                        <admin-categories-tags-fragment class="inner-addon">
                        </admin-categories-tags-fragment>
                    </admin-fragment-holder>
                `;
            case ActivePanel.MapSettings:
                return html`
                    <admin-fragment-holder
                        title="Map Settings"
                        description="Customize the look, feel and functionality of your map."
                        .addOn=${this.activeAddOn}
                        @close-fragment=${this.closeFragment}
                        >
                    <admin-advanced-features-fragment
                    class="inner-addon"
                        .selected_tab=${'Map Settings'}
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-advanced-features-fragment>
                    </admin-fragment-holder>
                `;
            case ActivePanel.PointSettings:
                return html`
                    <admin-fragment-holder
                        title="Point Settings"
                        description="Customize the look, feel and functionality of your points."
                        .addOn=${this.activeAddOn}
                        @close-fragment=${this.closeFragment}
                        >
                    <admin-advanced-features-fragment
                    class="inner-addon"
                        .selected_tab=${'Point Settings'}
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-advanced-features-fragment>
                    </admin-fragment-holder>
                `;
            case ActivePanel.IconSettings:
                return html`
                    <admin-fragment-holder
                        title="Icon Settings"
                        description="Customize the look, feel and functionality of your icons."
                        .addOn=${this.activeAddOn}
                        @close-fragment=${this.closeFragment}
                        >
                    <icon-settings
                    class="inner-addon"
                        .activeTab=${'icons'}
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </icon-settings>
                    </admin-fragment-holder>
                `;
            case ActivePanel.ShareSettings:
                return html`
                    <admin-fragment-holder
                        title="Share Settings"
                        description="Customize how your map is shared and viewed by others."
                        .addOn=${this.activeAddOn}
                        @close-fragment=${this.closeFragment}
                        >
                    <admin-share-settings-fragment
                    class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-share-settings-fragment>
                    </admin-fragment-holder>
                `;
            case ActivePanel.AdvancedFeatures:
                return html`
                    <admin-fragment-holder
                        title="Settings"
                        description="Customize the look, feel and functionality of your map and associated points."
                        .addOn=${this.activeAddOn}
                        @close-fragment=${this.closeFragment}
                        >
                    <admin-advanced-features-fragment
                    class="inner-addon"
                        @close-fragment="${this.closeFragment}"
                        @open-upgrade-dialog=${this.openUpgradeDialog}
                    >
                    </admin-advanced-features-fragment>
                    </admin-fragment-holder>
                `;
            case ActivePanel.PointsList:
                return html`
                    <admin-fragment-holder
                            title="Your Map Points"
                            description="Some map points may be hidden if you have a filter applied."
                            .addOn=${this.activeAddOn}
                            @close-fragment=${this.closeFragment}
                            >
                        <admin-points-list-fragment
                        class="inner-addon"
                        ></admin-points-list-fragment>
                    </admin-fragment-holder>
                `;
            case ActivePanel.AddOns:
                return html`
                <admin-fragment-holder
                            title="Add-Ons and Apps"
                            description="Features, extensions and apps to help you manage, monetize, and scale your maps."
                            .addOn=${this.activeAddOn}
                            @close-fragment=${this.closeFragment}
                            >
                    <admin-addons-list-fragment
                    class="inner-addon"
                        @open-upgrade-dialog=${this.openUpgradeDialog}>
                    </admin-addons-list-fragment>
                    </admin-fragment-holder>
                `;

            case ActivePanel.AiPointGeneration:
                return html`
                <admin-fragment-holder
                            title="AI Point Generation"
                            description="Turn Text, Images, and Prompts into Points on your map."
                            .addOn=${this.activeAddOn}
                            @close-fragment=${this.closeFragment}
                            >
                    <admin-ai-point-generation-fragment
                    class="inner-addon"
                        @open-upgrade-dialog=${this.openUpgradeDialog}>
                    </admin-ai-point-generation-fragment>
                    </admin-fragment-holder>
                `;
            default:
                return html``;
        }
    }

    drawerButtonClicked(e: CustomEvent) {
        e.stopPropagation();
        this.requestUpdate();

        if (this.renderInsights) {
            this.renderInsights = false;
        }

        if (this.renderSpreadsheet) {
            this.closeSpreadsheetModalComponent.panel = e.detail.panel;
            this.closeSpreadsheetModalComponent.action = e.detail.action;

            this.warnCloseSpreadsheet();
        }
        else {
            const boundAction = e.detail.action.bind(this);
            boundAction(e.detail.panel);
        }
    }

    async panelButtonClicked(activePanel: ActivePanel) {
        const addOn = topicData.activatedAddons.find((addon) => this.getAddOnPanel(addon.component) === activePanel);
        if (this.activePanel === activePanel) {
            this.hasOpenedPanel = false;
            logWithAmplitude(this.amplitude_callsite, `Admin Drawer Close: ${this.activePanel}`, {
                topic_id: topicData.topicId,
                topic_key: topicData.topicKey,
                drawer_name: this.activePanel,
            });
            this.activePanel = ActivePanel.None;
            this.activeAddOn = undefined;
            this.fullWidthPanelOpen = false;
        } else {
            this.hasOpenedPanel = true;
            if (this.fullWidthPanels.includes(activePanel)) {
                this.fullWidthPanelOpen = true;
            } else {
                this.fullWidthPanelOpen = false;
            }
            this.activePanel = activePanel;
            this.activeAddOn = addOn;
            logWithAmplitude(this.amplitude_callsite, `Admin Drawer Open: ${this.activePanel}`, {
                topic_id: topicData.topicId,
                topic_key: topicData.topicKey,
                drawer_name: this.activePanel,
            });
        }
    }

    closePanel() {
        this.activePanel = ActivePanel.None;
        this.hasOpenedPanel = false;
        this.activeAddOn = undefined;
    }

    get isMini() {
        return this.hasOpenedPanel || isDefined(this.mapComponentWrapper?.mapDrawer) || this.renderSpreadsheet || this.renderInsights || this.userPreview || this.mapComponentWrapper?.drawingIsActive;
    }

    private signInClick() {
        this.signinModalComponent.open = true;
    }

    private proxiBackpackClick() {
        logWithAmplitude(this.amplitude_callsite, 'Proxi Backpack Click');
        window.location.assign(`/#home`);
    }

    private accountPageClick() {
        logWithAmplitude(this.amplitude_callsite, 'Account Page Click');
        window.location.assign(`/#account`);
    }

    private async openSpreadsheetView() {
        pointCache.removeFilter();
        this.renderSpreadsheet = true;
        this.activePanel = ActivePanel.None;
        await this.requestUpdate();

        logWithAmplitude(this.amplitude_callsite, 'Open Spreadsheet', {
            topic_id: topicData.topicId,
            topic_key: topicData.topicKey,
        });
    }

    private async openInsights() {
        if (!featuresInfo.advancedInsightsEnabled) {
            this.openUpgradeDialog({ detail: { featureType: FeatureType.AdvancedInsights } });
            if (isDefined(this.upgradeDialogComponent)) {
            this.upgradeDialogComponent.addEventListener('close', () => {
                this.upgradeDialogComponent?.close();
            });}
        } else {
            this.renderInsights = true;
            this.activePanel = ActivePanel.None;
            this.requestUpdate();

            logWithAmplitude(this.amplitude_callsite, 'Open Insights', {
                topic_id: topicData.topicId,
                topic_key: topicData.topicKey,
            });
        }
    }

    private async onSpreadsheetSave() {
        this.closeSpreadsheet();
        if (isDefined(topicData.topic)) {
            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                true,
                true,
                challengeData.challenge ? challengeData.allCheckins : accountInfo.account_checkins
            );


            await translationData.instantiateTranslations(
                true
            );
        }
        await pointCache.refreshPoints();
    }

    private warnCloseSpreadsheet() {
        if (this.renderSpreadsheet) {
            this.closeSpreadsheetModalDialog.open = true;
        }
    }

    private handleCloseSpreadsheet(e: CustomEvent) {
        this.closeSpreadsheetModalDialog.close();

        const boundAction = e.detail.action.bind(this);
        boundAction(e.detail.panel);

        this.closeSpreadsheet();
    }

    private closeSpreadsheet() {
        this.renderSpreadsheet = false;
    }

    private closeInsights() {
        this.renderInsights = false;
    }

    removeCloseSpreadsheetModal() {
        this.closeSpreadsheetModalDialog.close();
    }

    learnClick() {
        logWithAmplitude(this.amplitude_callsite, 'Proxi Learn Click');
        window.open(`https://learn.proxi.co/`, '_blank');
    }

    private dismissWalkthrough() {
        logWithAmplitude(this.amplitude_callsite, 'Walkthrough Dismissed');
        this.walkthroughDismissed = true;
        localStorage.setItem('walkthrough-dismissed', 'true');
    }


    static get styles() {
        return css`
            :host {
                overflow: none;
                font-family: 'Montserrat', sans-serif;
                --main-font: 'Montserrat', sans-serif;
                --paper-input-container-shared-input-style_-_font-family: var(--main-font) !important;
                --paper-font-subhead_-_font-family: var(--main-font) !important;
                --mdc-typography-font-family: var(--main-font) !important;
                --mdc-typography-body1-font-family: var(--main-font) !important;
                --lumo-font-family: var(--main-font) !important;
                --paper-font-common-base_-_font-family: var(--main-font) !important;
            }

            .map__wrapper {
                position: relative;
                width: 100%;
                height: 100%;
                overflow: hidden;
            }

            mwc-dialog {
                /* this is because handontable sets the zindex of its columns and row headers to 160 */
                z-index: 200;
            }

            #rootContainer {
                display: flex;
                flex-direction: column;
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0px;
                left: 0px;
            }

            .main-container {
                height: auto;
                display: flex;
                flex-direction: column;
                flex:3 1 80%;
                max-width: 95vw;
            }

            .main-container.opened {
                flex-basis: 55%;
                transition: flex-basis 0.3s ease-in-out;
            }

            .main-container.full-width {
                flex-basis: 90%;
                transition: flex-basis 0.3s ease-in-out;
            }

            .main-section-container {
                flex: 1;
                display: flex;
                flex-direction: column;
                margin: 15px 30px 30px 30px;
                height: calc(100% - 45px);
                box-sizing: border-box;
                transition: flex-basis 0.3s ease-in-out;
                box-shadow: rgb(0 0 0 / 19%) 0px 10px 20px, rgb(0 0 0 / 23%) 0px 6px 6px;
                overflow: hidden;
            }

            #body {
                width: 100%;
                max-height: 100vh;
                flex: 1;
                display: flex;
                flex-direction: row;
                align-items: stretch;
                background-color: var(--theme-white-color);
            }

            .full-height-override {
                max-height: 100% !important;
            }

            .user-area {
                display: flex;
                padding: 10px 5px;
                align-items: center;
                justify-content: center;
                gap: 10px;
            }

            #insightsArea {
                height: calc(100% - 50px);
                padding: 2px;
                width: calc(100% - 10px);
            }

            .insights-close {
                position: absolute;
                right: 40px;
                margin-top: 5px;
                z-index: 2;
                cursor: pointer;
            }

            .map-name-section {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                margin-bottom: 10px;
                margin-top: 10px;
                text-align: center;
            }

            .stats-subtext {
                font-size: 10px;
                color: var(--theme-moon-color);
            }

            #drawerMain {
                display: flex;
                background-color: var(--theme-white-color);
                flex-direction: column;
                align-items: stretch;
                overflow-y: auto;
                overflow-x: hidden;
                padding: 0px;
                z-index: 1;
                flex: 1;
                border-right: 1px solid var(--theme-border-color);
                flex-basis: 20%;
                box-shadow: 1px 0 2px 0 rgba(0,0,40,.08),6px 0 10px 0 rgba(0,0,40,.08);

            }

            #drawerMain.mini {
                flex-basis: 5%;
                transition: flex-basis 0.5s ease-in-out;
                box-shadow: unset;
                border-right: 1px solid var(--main-primary-color);
                max-width: 55px;
            }

            .drawer-buttons {
                flex: 1;
                transition: width 0.3s ease-in-out;
                animation: fadeIn 0.5s;
                overflow-y: auto;
            }

            .drawer-buttons::-webkit-scrollbar {
                width: 5px;
            }

            .drawer-buttons::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            .drawer-buttons::-webkit-scrollbar-thumb {
                background: var(--theme-lighter-text-color);
            }

            .nav-content {
                display: flex;
                gap: 5px;
                align-content: center;
                flex-direction: row;
                align-items: center;
            }

            /* width */
            #drawerMain::-webkit-scrollbar {
                width: 0px;
            }

            /* Track */
            #drawerMain::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            #drawerMain::-webkit-scrollbar-thumb {
                background: var(--theme-lighter-text-color);
            }

            #drawerSub {
                position: relative;
                display: flex;
                flex-direction: row;
                width: 100%;
            }

            .statsSection {
                display: inline-flex;
                background-color: lightgray;
                width: 100%;
                flex-wrap: wrap;
                align-items: center;
                justify-content: center;
                align-content: center;
                border-radius: 0px;
            }

            .helpSection {
                display: inline-flex;
                background-color: lightgray;
                width: 100%;
                flex-wrap: wrap;
                align-items: center;
                justify-content: center;
                align-content: center;
                border-radius: 0px;
            }

            .miniHelp {
                display: flex;
                flex-direction: column;
            }

            .helpOption {
                display: flex;
                align-items: center;
                gap: 2px;
                padding: 5px;
                cursor: pointer;
                border-radius: 5px;
                background-color: var(--theme-background-color);
                color: var(--main-primary-color);
                --mdc-icon-size: 16px;
            }

            .backpack-sidebar__link {
                display: flex;
                align-items: center;
                text-decoration: none;
                justify-content: space-between;
                margin-top: 10px;
                font-weight: semi-bold;
                color: var(--main-primary-color);
            }

            .backpack-sidebar__creator-profile-link {
                color: var(--main-primary-color);
                text-decoration: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                padding-bottom: 14px;
            }

            .backpack-sidebar__creator-profile-link img {
                width: 1.5rem;
                height: 1.5rem;
                border-radius: 50%;
                margin-right: 10px
            }

            .header-buttons-wrapper {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                margin: 0.25rem;
            }

            .qr_code__wrapper {
                flex-direction: column;
                align-items: center;
                justify-content: center;
                margin: 0.25rem;
                z-index: 10;
                box-shadow: rgba(0, 0, 0, 0.25) 0px 0px 4px;
                right: 0px;
                background-color: var(--theme-white-color);
                position: absolute;
                left: 0px;
                width: 20vw;
                bottom: 0px;
                padding: 1rem;
                display: flex;
                font-size: 16px;
                font-weight: bold;
            }

            .insights-section {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-end;
                color: var(--theme-moon-color);
                gap: 0.25rem;
            }

            .add-insights-button {
                text-decoration: underline;
                margin-right: 0.25rem;
            }

            .drawerPanel {
                background-color: var(--theme-white-color);
                height: 100%;
                box-sizing: border-box;
                overflow: hidden; /* Hide content when closed */
                flex-basis: 0;
                transition: flex-basis 0.3 ease-in-out;
                box-shadow: 1px 0 2px 0 rgba(0,0,40,.08),6px 0 10px 0 rgba(0,0,40,.08);
            }

            .drawerPanel.closed {
                padding: 0; /* Ensure padding is also transitioned */
                flex-basis: 0%;
                transition: flex-basis 0.3 ease-in-out;
                animation: fadeOut 0.5s;
            }

            .drawerPanel.opened {
                border-top: var(--theme-border-color) 1px solid;
                flex-basis: 30%;
                max-width: 400px;
                transition: flex-basis 0.5s ease-in-out 0.2s;
            }

            .spreadsheetViewContainer {
                display: flex;
                flex-direction: column;
                justify-content: center;
                align-items: center;
                width: 100%;
                height: 100%;
                overflow: hidden;
            }

            .spreadsheetViewLabel {
                text-align: center;
                font-family: var(--main-font);
                font-size: 9px;
                font-weight: 500;
                padding-right: 5px;
            }

            .horizontal-flex {
                display: flex;
                flex-direction: row;
                width: 100%;
                height: 100%;
            }

            .people-on-map {
                padding: 0.5rem;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 0.25rem;
            }

            .customize-area {
                background-color: var(--main-invalid-color);
            }

            .data-area {
                background-color: var(--main-featured-color);
            }

            .privacy-area {
                background-color: var(--main-sky-color);
            }

            .customize-border {
                border-color: var(--main-invalid-color);
            }

            .data-border {
                border-color: var(--main-featured-color);
            }

            .privacy-border {
                border-color: var(--main-sky-color);
            }

            .add-on-border {
                border-color: var(--main-secondary-color);
            }

            .label {
                margin-top: 10%;
                text-align: center;
                font-size: 10px;
                color: var(--theme-white-color);
                font-weight: 500;
                padding-right: 5px;
            }

            .log-in-area {
                display: flex;
                margin: 15px 10px;
                padding: 10px;
                color: var(--main-invalid-color);
                border: 2px solid var(--main-invalid-color);
                border-radius: 5px;
                cursor: pointer;
            }

            .walkthrough-section {
                display: flex;
                gap: 12px;
                margin: 15px 10px;
                padding: 12px;
                background-color: var(--theme-background-color, #f8f9fa);
                border: 1px solid var(--theme-border-color, #e0e0e0);
                border-radius: 8px;
            }

            .walkthrough-icon {
                font-size: 24px;
                flex-shrink: 0;
                line-height: 1;
            }

            .walkthrough-content {
                display: flex;
                flex-direction: column;
                gap: 4px;
                flex: 1;
            }

            .walkthrough-title {
                margin: 0;
                font-size: 12px;
                font-weight: 600;
                color: var(--theme-text-color, #333);
            }

            .walkthrough-description {
                margin: 0;
                font-size: 10px;
                line-height: 1.2;
                color: var(--theme-subtext-color, #666);
            }

            .walkthrough-links {
                display: flex;
                flex-direction: column;
                gap: 6px;
                margin-top: 4px;
            }

            .walkthrough-link {
                font-size: 10px;
                color: var(--main-primary-color);
                text-decoration: none;
                font-weight: 500;
                transition: opacity 0.2s;
            }

            .walkthrough-link:hover {
                opacity: 0.7;
                text-decoration: underline;
            }

            .walkthrough-separator {
                font-size: 11px;
                color: var(--theme-subtext-color, #999);
                font-style: italic;
                margin: 2px 0;
            }

            .walkthrough-close {
                cursor: pointer;
                color: var(--theme-subtext-color, #999);
                font-size: 18px;
                flex-shrink: 0;
                transition: color 0.2s;
                align-self: flex-start;
            }

            .walkthrough-close:hover {
                color: var(--theme-text-color, #333);
            }

            .addPointButton {
                display: flex;
                flex-direction: column;
                align-items: center;
                background-color: var(--main-primary-color);
                color: var(--theme-white-color);
                border-style: none;
                margin: 6px;
                cursor: pointer;
                --mdc-theme-secondary: var(--main-primary-color);
                --mdc-theme-on-secondary: var(--theme-white-color);
            }

            .spreadsheetViewButton {
                background-color: var(--main-primary-color);
                color: var(--theme-white-color);
                border-style: none;
                margin: 6px 0;
                padding-right: 5px;
                width: 100%;
                height: 60px;
                --mdc-icon-size: 20px;
                border-radius: 10px 0px 0px 10px;
                cursor: pointer;
                display: block;
            }

            .hidden {
                display: none;
            }

            .spreadsheetViewIcon {
                color: var(---theme-white-color);
                --mdc-icon-size: 20px;
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 20px;
                margin-top: auto;
            }

            .editButtonIcon {
                --mdc-icon-size: 14px;
                margin-right: 0.25rem;
            }

            .gatedField {
                display: flex;
                align-items: center;
                justify-content: flex-end;
                margin-bottom: 3px;
            }

            .editStyleDialog {
                position: absolute;
                right: 0;
                top: 50px;
            }

            .inner-addon {
                display: flex;
                flex-direction: column;
                height: 100%;
                overflow-y: hidden;
            }

            .preview-hidden {
                display: none;
            }

            .preview-darker-background {
                background-color: var(--theme-focus-border-color);
            }

            .preview-desktop {
                margin: 2rem 3rem;
                height: calc(100% - ${unsafeCSS(heights.nav)} - 4rem);
            }

            .preview-mobile {
                height: calc(100% - ${unsafeCSS(heights.nav)} - 2rem);
                width: calc((100% - ${unsafeCSS(heights.nav)} - 2rem) / 4);
                margin-left: auto;
                margin-right: auto;
                font-size: 10px;
            }

            #drawerMain.preview-hidden {
                display: none;
            }

            @media only screen and (max-width: 550px) {
                .main-section-container {
                    margin: 8px;
                }

                .drawerPanel.opened {
                    min-width: calc(100% - 70px);
                    max-width: calc(100% - 70px);
                    border-top: none;
                }

                #shareSection {
                    display: none;
                }

                .spreadsheetViewButton {
                    display: none;
                }
            }
        `;
    }

    // User Tooltips & Helpers

   private async showNewUserTooltips() {
        const walktrhoughDismissed = getFromLocalStorage('walkthrough-dismissed');
        if (walktrhoughDismissed === 'true') {
            return;
        }
        // Query all elements needed for the walkthrough
        const mapWrapper = this.mapComponentWrapper as Element | undefined;
        const addPointButton = this.mapComponentWrapper?.shadowRoot?.querySelector('#addPointButton') as Element | undefined;
        const styleButton = this.shadowRoot?.querySelector('#style') as Element | undefined;
        const categoriesTagsButton = this.shadowRoot?.querySelector('#category') as Element | undefined;
        const shareButton = this.mapHeaderButtons?.shadowRoot?.querySelector('#shareBtn') as Element | undefined;
        const appsButton = this.shadowRoot?.querySelector('#add-ons') as Element | undefined;

        // Skip walkthrough if critical elements are missing
        if (!mapWrapper || !addPointButton) {
            console.warn('⚠️ Skipping walkthrough - critical elements not found');
            return;
        }

        // Create a step-by-step walkthrough for new users
        await registerWalkthroughAfterUpdate('new-user-onboarding', [
            {
                id: 'admin-welcome-map',
                targetElement: mapWrapper,
                position: 'center',
                backgroundImage: '/images/onboardingsteps/welcome.png',
                icon: '🎉',
                title: 'Welcome to Your Proxi Creator Portal!',
                content: 'This is your interactive map canvas. You can add places, customize the style, and share it with others. You can also build experiences on top of it like challenges, guides, and competitions. Let\'s explore the key features!',
            },
            {
                id: 'admin-add-points',
                targetElement: addPointButton,
                position: 'right',
                backgroundImage: '/images/onboardingsteps/add_places.png',
                icon: '📍',
                title: 'Add Points',
                content: 'Start building your map by adding points. You can add them manually using this button or by double clicking anywhere on the map. You can also import from a spreadsheet, or use AI to generate them from text — these options are available on the left side menu.',
            },
            {
                id: 'admin-customize-style',
                targetElement: styleButton,
                position: 'right',
                backgroundImage: '/images/onboardingsteps/style.png',
                icon: '🎨',
                title: 'Customize Your Map',
                content: 'Make your map stand out! Customize colors, add your logo, change the map style, and configure advanced settings.',
            },
            {
                id: 'admin-categories-tags',
                targetElement: categoriesTagsButton,
                position: 'right',
                backgroundImage: '/images/onboardingsteps/categories.png',
                icon: '🏷️',
                title: 'Organize with Categories & Tags',
                content: 'Use categories and tags to organize your points and set icons. This helps users find relevant information quickly and keeps your map structured.',
            },
            {
                id: 'admin-advanced-features',
                targetElement: appsButton,
                position: 'right',
                icon: '🚀',
                title: 'Explore Advanced Features',
                backgroundImage: '/images/onboardingsteps/advanced.png',
                content: 'Take your map to the next level with Proxi Apps! Create challenges, guides, votes, and more to engage your audience in new ways.',
            },
            {
                id: 'admin-share-map',
                targetElement: shareButton,
                position: 'bottom',
                backgroundImage: '/images/onboardingsteps/share.png',
                icon: '🔗',
                title: 'Share Your Map',
                content: 'Invite others to view or add points on your map. You can share it with specific people, make it public, or embed it on your website.',
            },
            {
                id: 'admin-end-walkthrough',
                targetElement: mapWrapper,
                position: 'center',
                backgroundImage: '/images/onboardingsteps/map_portal.png',
                icon: '✅',
                title: 'You\'re All Set!',
                onDismiss: () => {
                    show_confetti();
                    localStorage.setItem('walkthrough-dismissed', 'true');
                    this.walkthroughDismissed = true;
                },
                content: 'That wraps the tour of your Proxi Creator Portal. Jump in and start creating maps and experiences. Need help? Visit the help center or contact support. You’ll also see tooltips around the portal—click them to discover features and learn more.',
                actions: [
                        {
                            text: 'Book a Walkthrough with Us',
                            handler: () => {
                                const link = 'https://app.proxi.co/demo';
                                window.open(link, '_blank');
                            }
                        }
                ]
            }
        ], this);
    }
}
